import React, {useEffect, useLayoutEffect, useRef, useState} from "react";
import {FlyStage} from "../games/demo/stage";
import {ZedSays} from "../games/demo/zed";
import {flyPieces} from "../games/demo/fly";
import {page, card, primaryBtn, darkBtn, inputStyle} from "../games/demo/ui";

// The imgoingto.party landing page: a 90-second taste of the experience, hosted by Zed, before the business part. He
// introduces himself and narrates over the promo video → a fact about you → your nickname → one question about it →
// what a show needs and what brings you here → book (or the waitlist). On the intro, Skip skips the video; after it, Skip
// jumps straight to the details.

// the promo, cut for the landing (~11.4s): phones, Zed's big screen, the crowd, and it ends on the dancing
const PROMO = '/landing-promo.mp4';

// The opening: his hello alone in the middle, then these over the promo video.
const HELLO = "Finally. You're here.";
// When each narration line starts, in seconds into the landing cut (~11.4s): evenly through the clip.
const CUES = [0, 5.7];
const NARRATION = ["I'm Zed. The host of a party game actually worth playing.", "Give me a room full of strangers. I'll make them friends."];

const NEEDS = [
    {icon: '⏱', label: 'Length', text: '45–60 minutes'},
    {icon: '👥', label: 'Crowd', text: '20–40 players'},
    {icon: '🕺', label: 'Space', text: 'room to stand and move around'},
    {icon: '📱', label: 'Devices', text: "guests' own phones"},
    {icon: '📺', label: 'Screen & sound', text: 'yours, or our portable setup'},
];

// When the network fails outright (the server falls back on its own otherwise).
const OFFLINE = {nickname: 'Unverified Legend',
    question: {question: 'What do you do at parties, Unverified Legend?', options: ['Find the dog', 'Guard the snacks', 'Leave early']},
    answers: ['The dog is avoiding you. I understand the dog.', 'The snacks are not yours. Nothing is.',
        'Nobody leaves my parties early. Nobody.']};

const postJSON = (path, body) => fetch(path, {method: 'POST', headers: {'Content-Type': 'application/json'}, body: JSON.stringify(body)});

// Anonymous funnel counts (one random id per visit), to see where people finish the taste or bail.
const VISIT = Math.random().toString(36).slice(2, 10);
const track = (name, data) => { postJSON('/landing/event', {name, visit: VISIT, data}).catch(() => {}); };

const FINE_POINTER = window.matchMedia && window.matchMedia('(pointer: fine)').matches; // a mouse, not a finger

const choiceBtn = {...darkBtn, textAlign: 'left', textTransform: 'none', letterSpacing: 0, fontSize: 20, padding: '16px 18px'};

export const Landing = () => {
    // ?step=fact (or meet, ask, book…) opens a screen directly, for previewing
    const [step, setStep] = useState(() => {
        const s = new URLSearchParams(window.location.search).get('step');
        return ['fact', 'meet', 'ask', 'waitlist', 'book', 'done'].includes(s) ? s : 'intro';
    });
    const [intent, setIntent] = useState(null);
    const [fact, setFact] = useState('');
    const [zed, setZed] = useState(() => (step === 'meet' ? OFFLINE : null)); // ?step=meet previews with stand-in lines
    const [lead, setLead] = useState(null); // 'host' or 'waitlist', once sent
    const [stage, setStage] = useState(0); // bumped on Back: a new stage that shows its screen without a transition
    const [fields, setFields] = useState({}); // the booking form, kept here so going back doesn't lose it
    const [email, setEmail] = useState('');

    useEffect(() => { document.title = 'Party Machine'; track('arrive'); }, []);

    // each screen change records the screen left and the time spent on it, so a visit reads as a timeline
    const shown = useRef({step, at: performance.now()});
    useEffect(() => {
        const prev = shown.current;
        if (prev.step === step) return;
        track('step', {from: prev.step, to: step, ms: Math.round(performance.now() - prev.at)});
        shown.current = {step, at: performance.now()};
    }, [step]);

    // The phone's own back gesture steps back through the business screens: the forms get history entries, while
    // the taste (intro, fact, audition) and the sign-off replace theirs, so back never replays the audition or reopens
    // a form you already sent.
    useEffect(() => {
        const entry = {landingStep: step};
        if (['book', 'waitlist'].includes(step)) window.history.pushState(entry, '');
        else window.history.replaceState(entry, '');
    }, [step]);
    useEffect(() => {
        // the browser already animates its back gesture, so the screen swaps in place instead of flying (a fresh stage)
        const onPop = (e) => { if (e.state && e.state.landingStep) { setStage(n => n + 1); setStep(e.state.landingStep); } };
        window.addEventListener('popstate', onPop);
        return () => window.removeEventListener('popstate', onPop);
    }, []);

    const meet = async (f, ms) => {
        setFact(f);
        setStep('meet');
        track('fact', {len: f.length, ms});
        try {
            const res = await postJSON('/landing/zed', {fact: f, visit: VISIT});
            setZed(res.ok ? await res.json() : OFFLINE);
        } catch (e) {
            setZed(OFFLINE);
        }
    };
    const sendLead = async (kind, fields) => {
        const res = await postJSON('/landing/lead', {kind, intent, fact, nickname: zed && zed.nickname, fields}).catch(() => null);
        if (!res || !res.ok) return false;
        setLead(kind);
        setStep('done');
        track(`lead:${kind}`);
        return true;
    };

    const screens = {
        intro: <Intro onDone={() => setStep('fact')} />,
        fact: <Fact onSubmit={meet} />,
        meet: <Meet zed={zed} onDone={pick => { track('answer', {pick}); setStep('ask'); }} />,
        ask: <Ask nickname={zed && zed.nickname} onPick={i => { setIntent(i); track(`intent:${i}`); setStep(i === 'host' ? 'book' : 'waitlist'); }} />,
        waitlist: <Waitlist intent={intent} onSend={e => sendLead('waitlist', {email: e})} email={email} setEmail={setEmail} />,
        book: <Book onSend={f => sendLead('host', f)} fields={fields} setFields={setFields} />,
        done: <Done lead={lead} nickname={zed && zed.nickname} sent={lead === 'host' ? fields : {email}} />,
    };
    const skippable = ['intro', 'fact', 'meet'].includes(step);

    return <div style={{minHeight: '100dvh', background: page.background}}>
        <div style={{maxWidth: 520, margin: '0 auto'}}>
            <FlyStage key={stage} still={stage > 0} screenKey={step}>{screens[step]}</FlyStage>
        </div>
        {/* the intro's skip only skips the video; later ones go straight to the details */}
        {skippable && <button onClick={() => { track(`skip:${step}`); setStep(step === 'intro' ? 'fact' : 'ask'); }} style={{position: 'fixed', left: '50%', transform: 'translateX(-50%)',
            bottom: 'calc(10px + env(safe-area-inset-bottom))', zIndex: 30, background: 'none', border: 'none', color: '#f3ead8', opacity: 0.55,
            fontFamily: page.fontFamily, fontSize: 15, padding: '8px 16px', cursor: 'pointer'}}>{step === 'intro' ? 'Skip video ›' : 'Skip ›'}</button>}
    </div>;
};

// the background lives on the full-width wrapper, so on wide screens the column doesn't show an edge; the bottom
// padding leaves room for Skip
const screen = {...page, background: 'transparent', paddingBottom: 'calc(56px + env(safe-area-inset-bottom))'};
// what you tap or type sits at the bottom of every screen, in thumb reach, with Zed at the top
const bottom = {display: 'flex', flexDirection: 'column', gap: 14, marginTop: 'auto'};

// hello (Zed alone, centered) → watch (he moves up, the promo plays, he narrates) → on to your fact. Each narration
// line starts on its scene in the video (but never cuts off the line before it), and the fact screen comes once the
// video has ended on its last frame. If the video can't play (Low Power Mode blocks autoplay), the lines run on reading
// holds instead. A tap anywhere moves on sooner.
const Intro = ({onDone}) => {
    const [phase, setPhase] = useState('hello');
    const [said, setSaid] = useState(0);
    const [typed, setTyped] = useState(-1); // the last narration line he finished typing
    const typedAt = useRef(0);
    const [talking, setTalking] = useState(false);
    const [video, setVideo] = useState(false);
    const [clip, setClip] = useState({t: 0, dur: 0, ended: false, stalled: false});
    // the video fades out on its own before the screen moves on, then hides outright, so the screen's own exit fade
    // (which starts from full opacity) has nothing visible to replay
    const [fading, setFading] = useState(false);
    const [gone, setGone] = useState(false);
    const finish = () => { setFading(true); setTimeout(() => setGone(true), 620); setTimeout(onDone, 650); };
    const line = !talking ? HELLO : NARRATION[said];
    // one thing at a time: he glides up (still showing his hello), then starts talking, then the video fades in
    // the whole clip downloads during his hello and plays from memory, so it can't stall mid-play on a slow phone
    const promoUrl = usePromoDownload();
    const [due, setDue] = useState(false);
    useEffect(() => {
        if (phase !== 'watch') return undefined;
        const talk = setTimeout(() => setTalking(true), 1050);
        const show = setTimeout(() => setDue(true), 1500);
        const fallback = setTimeout(() => setVideo(true), 3000); // still downloading: stream it instead
        return () => { clearTimeout(talk); clearTimeout(show); clearTimeout(fallback); };
    }, [phase]);
    useEffect(() => { if (due && promoUrl) setVideo(true); }, [due, promoUrl]);
    const src = useRef(PROMO); // fixed once it starts playing, so a late download doesn't swap it mid-play
    if (!video && promoUrl) src.current = promoUrl;
    useEffect(() => {
        if (!video) return undefined;
        const t = setTimeout(() => setClip(c => (c.t > 0 ? c : {...c, stalled: true})), 2500);
        return () => clearTimeout(t);
    }, [video]);
    useEffect(() => {
        if (typed !== said || typed < 0) return undefined;
        const last = said + 1 >= NARRATION.length;
        // the last line waits for the clip's final moments: it fades out while still playing, not on a frozen frame
        const nearEnd = clip.ended || (clip.dur > 0 && clip.t >= clip.dur - 0.75);
        const ready = clip.stalled || (last ? nearEnd : clip.t >= CUES[said + 1]);
        if (!ready) return undefined;
        const hold = clip.stalled ? 900 + NARRATION[said].length * 35 : last ? 0 : 600; // the intro ends when the video does
        const t = setTimeout(() => (last ? finish() : setSaid(said + 1)), Math.max(0, hold - (performance.now() - typedAt.current)));
        return () => clearTimeout(t);
    }, [typed, said, clip]); // eslint-disable-line react-hooks/exhaustive-deps
    const finished = () => {
        if (phase === 'hello') { setTimeout(() => setPhase('watch'), 350); return; } // the hello's hold is his glide up
        if (!talking) return;
        typedAt.current = performance.now();
        setTyped(said);
    };
    return <div style={{...screen, gap: 14, isolation: 'isolate', justifyContent: phase === 'hello' ? 'center' : 'flex-start'}}
                onClick={() => (phase === 'hello' ? setPhase('watch') : onDone())}>
        <Promo src={src.current} show={video && !gone} fading={fading} onTime={(t, dur) => setClip(c => ({...c, t, dur}))} onEnded={() => setClip(c => ({...c, ended: true}))}
                         onError={() => setClip(c => ({...c, stalled: true}))} />
        <ZedSays line={line} onDone={finished} at={phase} />
    </div>;
};

// After the taste, the business part: what a show needs, then what brings you here. Like the question, your pick lights
// up and holds a beat before moving on.
const ASK_OPTIONS = [{key: 'host', icon: '🎤', text: 'Host Zed at your event'}, {key: 'play', icon: '🎟', text: "Find Zed's next appearance"}];
const Ask = ({nickname, onPick}) => {
    const [picked, setPicked] = useState(null);
    const pick = (key) => {
        if (picked) return;
        setPicked(key);
        setTimeout(() => onPick(key), 550);
    };
    return <div style={{...screen, gap: 14}}>
        <ZedSays line={nickname ? "Fine. You're in. Here are the details." : 'Here are the details.'} />
        <div style={{display: 'flex', flexDirection: 'column', gap: 13, padding: '6px 4px 0'}}>
            {NEEDS.map(n => <div key={n.icon} style={{display: 'flex', alignItems: 'baseline', gap: 12, fontSize: 21, lineHeight: 1.25}}>
                <span style={{width: 30, textAlign: 'center', flexShrink: 0}}>{n.icon}</span>
                <span><b style={{fontWeight: 800}}>{n.label}:</b> <span style={{opacity: 0.8}}>{n.text}</span></span>
            </div>)}
        </div>
        <div style={bottom}>
            <div style={{fontSize: 26, fontWeight: 800, textAlign: 'center', marginBottom: 2}}>What brings you here?</div>
            {ASK_OPTIONS.map(o => <button key={o.key} onClick={() => pick(o.key)} style={picked === o.key
                ? {...choiceBtn, ...primaryBtn, textAlign: 'left', textTransform: 'none', letterSpacing: 0, fontSize: 20, padding: '16px 18px'}
                : {...choiceBtn, opacity: picked ? 0.4 : 1, transition: 'opacity 200ms ease'}}>
                {picked === o.key ? '✓' : o.icon}{'\u00a0 '}{o.text}
            </button>)}
        </div>
    </div>;
};

// The promo, looping behind the screen (a blurred copy fills the sides on wide screens).
// Fetches the whole promo into memory (a blob URL); null until it's there.
const usePromoDownload = () => {
    const [url, setUrl] = useState(null);
    useEffect(() => {
        let live = true, made = null;
        fetch(PROMO).then(r => r.blob()).then(b => { if (live) { made = URL.createObjectURL(b); setUrl(made); } }).catch(() => {});
        return () => { live = false; if (made) URL.revokeObjectURL(made); };
    }, []);
    return url;
};

// The promo behind the intro. It's mounted (and loading) from the start, invisible and paused, and plays from its
// first frame when `show` turns on. On a phone it fills the screen (a sliver cropped off the sides); on a wide screen
// it fits, with a blurred copy filling the sides.
const Promo = ({src, show, fading, onTime, onEnded, onError}) => {
    const blurred = useRef(null), main = useRef(null);
    const phone = window.innerWidth / window.innerHeight < 0.75;
    // iOS ignores preload, so warm it up: a muted play() that's paused straight away makes it download now
    useEffect(() => {
        [blurred.current, main.current].forEach(v => v && v.play().then(() => { if (v.dataset.shown !== '1') v.pause(); }).catch(() => {}));
    }, []);
    useEffect(() => {
        if (!show) return;
        [blurred.current, main.current].forEach(v => {
            if (!v) return;
            v.dataset.shown = '1';
            v.currentTime = 0;
            v.play().catch(() => onError && onError());
        });
    }, [show]); // eslint-disable-line react-hooks/exhaustive-deps
    const video = {position: 'fixed', top: 0, left: 0, width: '100%', height: '100dvh', pointerEvents: 'none',
        visibility: show ? 'visible' : 'hidden'}; // hidden, not opacity 0: the screen's own fade-in would override an opacity
    // a class, not an inline animation: inline would override the fade-out when the screen leaves
    const fade = fading ? 'fade-out-slow' : show ? 'fade-in-slow' : undefined;
    return <>
        {/* the blurred copy only shows on wide screens, beside the fitted video; a phone doesn't load it */}
        {!phone && <video ref={blurred} src={src} muted playsInline preload="auto" aria-hidden="true" data-fly="fade" className={fade}
               style={{...video, objectFit: 'cover', filter: 'blur(28px) brightness(0.45)', transform: 'scale(1.15)', zIndex: -2}} />}
        <video ref={main} src={src} muted playsInline preload="auto" aria-hidden="true" data-fly="fade" className={fade}
               onTimeUpdate={e => onTime && onTime(e.target.currentTime, e.target.duration || 0)} onEnded={onEnded} onError={onError}
               style={{...video, objectFit: phone ? 'cover' : 'contain', zIndex: -1}} />
        <div data-fly="fade" className={fade} style={{...video, zIndex: -1, background: 'linear-gradient(transparent 55%, rgba(0,0,0,0.6))'}} />
    </>;
};

const Fact = ({onSubmit}) => {
    const [fact, setFact] = useState('');
    const shownAt = useRef(performance.now());
    // a tap meant for the screen before can't land as a submit while this one is still arriving
    const submit = () => {
        const ms = Math.round(performance.now() - shownAt.current);
        if (ms > 500 && fact.trim()) onSubmit(fact.trim(), ms);
    };
    return <div style={{...screen, gap: 14}}>
        <ZedSays line="Want in? Impress me." />
        <div style={bottom}>
            {/* no autofocus on phones: the keyboard opening mid-transition shifts the page under the flying pieces */}
            {/* no autofill or suggestion bar: one tap on a suggestion shouldn't be able to fill and send it */}
            <input type="text" name="zed-fact" autoComplete="off" autoCorrect="off" spellCheck={false}
                   className="demo-input" style={inputStyle} value={fact} maxLength={140} autoFocus={FINE_POINTER}
                   onChange={e => setFact(e.target.value)} onKeyDown={e => e.key === 'Enter' && submit()} placeholder="A fact about yourself" />
            <button style={{...primaryBtn, opacity: fact.trim() ? 1 : 0.5}} onClick={submit}>Submit</button>
        </div>
    </div>;
};

// The audition, on one screen: "Interesting. I guess. I shall call you..." types straight away and waits there for
// the AI (a beat of suspense), then your nickname as his next line; then he cross-examines you (the options fly in), and the moment you
// answer, his verdict: did you impress him, and why.
const NAMING = 'Interesting. I guess. I shall call you...';
const Meet = ({zed, onDone}) => {
    const [said, setSaid] = useState(0); // lines finished typing (and held)
    const [picked, setPicked] = useState(null);
    const options = useRef(null);
    const lines = [NAMING, ...(zed ? [`${zed.nickname.toUpperCase()}.`, zed.question.question] : [])];
    const line = picked !== null ? zed.answers[picked] : lines[Math.min(said, lines.length - 1)];
    const asking = said >= 2 && zed;
    const finished = () => {
        if (picked !== null) { setTimeout(() => onDone(picked), 1400); return; }
        if (said === 0) setTimeout(() => setSaid(1), 800); // a beat on "...", even when the nickname is already here
        else if (said === 1) setTimeout(() => setSaid(2), 1100);
    };
    useLayoutEffect(() => { if (asking) flyPieces(options.current, 'fly-in'); }, [asking]);
    return <div style={{...screen, gap: 14}}>
        <ZedSays line={line} onDone={finished} />
        {asking && <div ref={options} style={bottom}>
            {zed.question.options.map((o, i) => (
                <button key={o} onClick={() => picked === null && setPicked(i)} style={picked === i
                    ? {...primaryBtn, fontSize: 20, padding: '16px 12px', textTransform: 'none', letterSpacing: 0}
                    : {...darkBtn, fontSize: 20, padding: '16px 12px', textTransform: 'none', letterSpacing: 0,
                        opacity: picked === null ? 1 : 0.4, transition: 'opacity 200ms ease'}}>
                    {picked === i ? `✓ ${o}` : o}
                </button>
            ))}
        </div>}
    </div>;
};

const Waitlist = ({intent, onSend, email, setEmail}) => {
    const [err, setErr] = useState('');
    const send = async () => {
        if (!email.includes('@')) { setErr('That is not an email.'); return; }
        if (!(await onSend(email.trim()))) setErr('Something went wrong. Try again.');
    };
    return <div style={{...screen, gap: 14}}>
        <ZedSays line={intent === 'play' ? "Shows are coming. I'll find you." : 'Stay close. Or host me.'} />
        <div style={bottom}>
            <input type="email" className="demo-input" style={inputStyle} value={email} onChange={e => setEmail(e.target.value)}
                   onKeyDown={e => e.key === 'Enter' && send()} placeholder="you@example.com" />
            {err && <div style={{color: '#ff8a8a', textAlign: 'center', fontWeight: 700}}>{err}</div>}
            <button style={primaryBtn} onClick={send}>Join the waitlist</button>
        </div>
    </div>;
};

// Kept to an email and one optional line; we follow up and ask the rest ourselves.
const BOOK_ROWS = [
    [{key: 'email', label: 'Email', type: 'email'}],
    [{key: 'occasion', label: "What's the occasion? (optional)"}],
];

const Book = ({onSend, fields, setFields}) => {
    const [err, setErr] = useState('');
    const send = async () => {
        if (!(fields.email || '').includes('@')) { setErr('I need an email to find you.'); return; }
        if (!(await onSend(fields))) setErr('Something went wrong. Try again.');
    };
    return <div style={{...screen, gap: 12}}>
        <ZedSays line="Leave your email. I'll be in touch." />
        <div style={{...bottom, gap: 12}}>
            {BOOK_ROWS.map(row => <div key={row[0].key} style={{display: 'flex', gap: 12}}>
                {row.map(f => <input key={f.key} type={f.type || 'text'} inputMode={f.mode} className="demo-input"
                                     style={{...inputStyle, fontSize: 19, padding: '14px 16px', minWidth: 0, flex: 1}}
                                     placeholder={f.label} value={fields[f.key] || ''} onChange={e => setFields(v => ({...v, [f.key]: e.target.value}))} />)}
            </div>)}
            {err && <div style={{color: '#ff8a8a', textAlign: 'center', fontWeight: 700}}>{err}</div>}
            <button style={{...primaryBtn, marginTop: 6}} onClick={send}>Send it</button>
        </div>
    </div>;
};

// After a booking or signup: what they sent (so a mistyped email gets caught), what happens next, and somewhere to go.
const INSTAGRAM = 'https://instagram.com/partytechhq';
const iconStyle = {width: 22, height: 22, flexShrink: 0};
const InstagramIcon = () => <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" style={iconStyle} aria-hidden="true">
    <rect x="3" y="3" width="18" height="18" rx="5" /><circle cx="12" cy="12" r="4" /><circle cx="17.5" cy="6.5" r="1" fill="currentColor" stroke="none" />
</svg>;
const ShareIcon = () => <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" style={iconStyle} aria-hidden="true">
    <path d="M12 3v12" /><path d="M7.5 7.5 12 3l4.5 4.5" /><path d="M5 11v8a2 2 0 0 0 2 2h10a2 2 0 0 0 2-2v-8" />
</svg>;
const withIcon = {display: 'flex', alignItems: 'center', justifyContent: 'center', gap: 10};
const SENT_LABELS = {email: 'Email', occasion: 'Occasion'};
const Done = ({lead, nickname, sent}) => {
    const share = () => {
        const data = {title: 'Party Machine', text: 'Zed hosts a party game actually worth playing.', url: window.location.origin};
        if (navigator.share) navigator.share(data).catch(() => {});
        else navigator.clipboard && navigator.clipboard.writeText(data.url);
    };
    const rows = Object.keys(SENT_LABELS).filter(k => (sent[k] || '').trim());
    return <div style={{...screen, gap: 14}}>
        <ZedSays line={lead === 'host' ? `Received${nickname ? `, ${nickname.toUpperCase()}` : ''}. Prepare your guests.` : "You're on the list. I'll find you."} />
        <div style={{fontSize: 26, fontWeight: 800, textAlign: 'center', marginTop: 6}}>
            {lead === 'host' ? 'Booking request sent!' : "You're on the waitlist!"}
        </div>
        <div style={{...card, padding: '16px 18px', display: 'flex', flexDirection: 'column', gap: 8, fontSize: 18, lineHeight: 1.3}}>
            {rows.map(k => <div key={k}><b style={{fontWeight: 800}}>{SENT_LABELS[k]}:</b> <span style={{opacity: 0.8, overflowWrap: 'anywhere'}}>{sent[k]}</span></div>)}
        </div>
        <div style={{textAlign: 'center', opacity: 0.6, fontSize: 16}}>
            {lead === 'host' ? "We'll reach out soon." : "We'll email you when Zed's next appearance is set."}
        </div>
        <div style={{...bottom, flexDirection: 'row', gap: 12}}>
            <button style={{...darkBtn, ...withIcon, flex: 1, width: 'auto'}} onClick={share}><ShareIcon />Share</button>
            <a href={INSTAGRAM} target="_blank" rel="noopener noreferrer"
               style={{...primaryBtn, ...withIcon, flex: 1, width: 'auto', textDecoration: 'none', boxSizing: 'border-box', textTransform: 'none', letterSpacing: 1}}>
                <InstagramIcon />Follow
            </a>
        </div>
    </div>;
};
